import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Badge } from '../../components/ui/badge';
import { Calendar, TrendingUp, Award, Hash } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';

const PERIODS = [
  { v: 'today', l: 'Today' },
  { v: '7d', l: '7 days' },
  { v: '14d', l: '14 days' },
  { v: '30d', l: '30 days' },
  { v: 'all', l: 'Depuis le début' },
];

export default function ScriptsAnalytics({ embedded = false }) {
  const [data, setData] = useState({ most_used: [], best_converting: [] });
  const [accounts, setAccounts] = useState([]);
  const [period, setPeriod] = useState('30d');

  const load = useCallback(async () => {
    try {
      const params = period === 'all' ? {} : { period };
      const [a, an] = await Promise.all([
        api.get('/reports/scripts-analytics', { params }),
        api.get('/accounts'),
      ]);
      // Keep the safe shape even if the API omits a key — every render path below
      // reads data.most_used / data.best_converting unconditionally.
      setData({ most_used: [], best_converting: [], ...(a.data || {}) });
      setAccounts(Array.isArray(an.data) ? an.data : []);
    } catch (e) {
      toast.error(apiError(e, 'Failed to load analytics'));
    }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const accMap = useMemo(() => Object.fromEntries(accounts.map(a => [a.id, a])), [accounts]);

  const totalUses = useMemo(() => data.most_used.reduce((s, x) => s + (x.uses || 0), 0), [data.most_used]);
  const totalRevenue = useMemo(() => data.best_converting.reduce((s, x) => s + (x.revenue || 0), 0), [data.best_converting]);

  const wrapperCls = embedded ? 'fade-in' : 'p-6 md:p-8 max-w-7xl mx-auto fade-in';

  return (
    <div className={wrapperCls} data-testid="scripts-analytics-page">
      {!embedded && <PageHeader title="Scripts Analytics" subtitle="Most-used scripts & best converters" onRefresh={load} />}

      {/* Period filter */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="period-bar">
        <Calendar size={14} className="text-slate-500 ml-1" />
        {PERIODS.map(p => (
          <button
            key={p.v}
            data-testid={`period-${p.v}`}
            onClick={() => setPeriod(p.v)}
            className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${period === p.v ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'}`}
          >
            {p.l}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-3 mb-5">
        <SummaryCard icon={<Hash size={14} />} label="Scripts tracked" value={data.most_used.length} accent="text-slate-300" testid="card-tracked" />
        <SummaryCard icon={<TrendingUp size={14} />} label="Total uses" value={totalUses} accent="text-indigo-300" testid="card-uses" />
        <SummaryCard icon={<Award size={14} />} label="Attributed revenue" value={`${totalRevenue.toLocaleString()} €`} accent="text-emerald-400" testid="card-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel
          title="Most used scripts"
          icon={<TrendingUp size={14} className="text-indigo-400" />}
          rows={data.most_used}
          accMap={accMap}
          metric="uses"
          metricLabel="uses"
          metricColor="text-indigo-300"
          testid="most-used-panel"
          empty="No scripts used yet"
        />
        <Panel
          title="Best converting scripts"
          icon={<Award size={14} className="text-emerald-400" />}
          rows={data.best_converting.filter(s => s.revenue > 0)}
          accMap={accMap}
          metric="revenue"
          metricLabel="€"
          metricColor="text-emerald-400"
          testid="best-converting-panel"
          empty="No attributed sales yet — try assigning conversations to scripts that link to DropFans"
        />
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-xl font-bold ${accent}`}>{value}</div>
      </div>
    </div>
  );
}

function Panel({ title, icon, rows, accMap, metric, metricLabel, metricColor, testid, empty }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-slate-500" data-testid={`${testid}-empty`}>{empty}</div>
      ) : (
        <ol className="divide-y divide-white/5">
          {rows.map((r, i) => {
            const acc = r.account_id ? accMap[r.account_id] : null;
            const val = metric === 'revenue'
              ? `${(r.revenue || 0).toLocaleString()} ${metricLabel}`
              : `${r[metric] || 0} ${metricLabel}`;
            return (
              <li key={r.id} className="px-5 py-3 flex items-center gap-3" data-testid={`${testid}-row-${r.id}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${i === 0 ? 'bg-amber-400/20 text-amber-300' : i === 1 ? 'bg-slate-400/20 text-slate-300' : i === 2 ? 'bg-orange-700/20 text-orange-400' : 'bg-white/5 text-slate-500'}`}>{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{r.title || 'Untitled'}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {r.tag && <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]">{r.tag}</Badge>}
                    {acc && <span className="text-[10px] text-slate-500">{acc.name}</span>}
                    {metric === 'revenue' && r.sales_count > 0 && <span className="text-[10px] text-slate-500">· {r.sales_count} sales</span>}
                    {metric === 'uses' && r.revenue > 0 && <span className="text-[10px] text-emerald-400">· {r.revenue.toLocaleString()} € attrib.</span>}
                  </div>
                </div>
                <div className={`text-sm font-bold ${metricColor} font-display`}>{val}</div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
